import Swiper from 'swiper';
import { Navigation, Pagination, Mousewheel, A11y, EffectFade, Keyboard, Autoplay, Scrollbar, EffectCoverflow } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import 'swiper/css/effect-coverflow';
import 'swiper/css/autoplay';
import 'swiper/css/scrollbar';
import 'swiper/css/a11y';

const STRINGS = document.body.dataset.slider.split(',');
const inBackend = document.body.classList.contains('neos-backend');

export const defaultSwiperOptions = {
    modules: [Navigation, Pagination, Mousewheel, A11y, EffectFade, Keyboard, Autoplay, Scrollbar, EffectCoverflow],
    keyboard: !inBackend,
    simulateTouch: !inBackend,
    a11y: {
        enabled: true,
        paginationBulletMessage: STRINGS[0],
        prevSlideMessage: STRINGS[1],
        nextSlideMessage: STRINGS[2],
    },
};

const swiperInstances = {
    all: [],
    desktopOnly: [],
    mobileOnly: [],
};

initSlidersForNodes();
initOptionalDesktopSlider();
initOptionalMobileSlider();
initTeaserCarousel();
initQuoteSlider();

export function initSlidersForNodes() {
    document.querySelectorAll('.image-slider').forEach((sliderElement) => {
        initSlider(sliderElement, 'image-slide-item', {
            loop: false,
            spaceBetween: 90,
            effect: 'fade',
            pagination: {
                el: sliderElement.querySelector('.swiper-pagination'),
                dynamicBullets: true,
                clickable: true,
            },
        });
    });

    document.querySelectorAll('.page-slider').forEach((sliderElement) => {
        initSlider(sliderElement, 'swiper-slide', {
            effect: 'fade',
            autoplay: true,
            loop: true,
            speed: 1000,
            pagination: {
                el: sliderElement.querySelector('.swiper-pagination'),
                clickable: true,
            },
        });
    });

    document.querySelectorAll('.rooms-slider').forEach((sliderElement) => {
        initSlider(sliderElement, 'rooms-slider__item', {
            slidesPerView: 1.1,

            breakpoints: {
                1025: {
                    slidesPerView: 3,
                    spaceBetween: 30,
                },
                768: {
                    slidesPerView: 2,
                    spaceBetween: 30,
                },
            },
            pagination: {
                el: sliderElement.querySelector('.swiper-pagination'),
                clickable: true,
                dynamicBullets: true,
            },
        });
    });

    document.querySelectorAll('.packages-slider').forEach((sliderElement) => {
        initSlider(sliderElement, 'packages-slider__item', {
            slidesPerView: 1.1,

            breakpoints: {
                1025: {
                    slidesPerView: 3,
                    spaceBetween: 30,
                },
                768: {
                    slidesPerView: 2,
                    spaceBetween: 30,
                },
            },
            pagination: {
                el: sliderElement.querySelector('.swiper-pagination'),
                clickable: true,
                dynamicBullets: true,
            },
        });
    });

    document.querySelectorAll('.text-with-image-slider').forEach((sliderElement) => {
        initSlider(sliderElement, 'text-with-image-item', {
            slidesPerView: 1,
            pagination: {
                el: sliderElement.querySelector('.swiper-pagination'),
                clickable: true,
                dynamicBullets: true,
            },
        });
    });

    document.querySelectorAll('.linked-packages-slider').forEach((sliderElement) => {
        initSlider(sliderElement, 'package-teaser', {
            slidesPerView: 1,
            autoHeight: true,

            breakpoints: {
                990: {
                    slidesPerView: 2,
                    spaceBetween: 30,
                    autoHeight: false,
                },
            },
            pagination: {
                el: sliderElement.querySelector('.swiper-pagination'),
                clickable: true,
                dynamicBullets: true,
            },
        });
    });
}

export function initSlider(sliderElement, slideClass, options) {
    const wrapper = sliderElement.querySelector('.swiper-wrapper');

    if (!wrapper) {
        // no wrapper found within swiper element
        return;
    }

    const swiperInstance = new Swiper(sliderElement, {
        ...defaultSwiperOptions,
        slideClass: slideClass,
        navigation: {
            nextEl: sliderElement.querySelector('.swiper-button-next'),
            prevEl: sliderElement.querySelector('.swiper-button-prev'),
        },
        ...options,
    });

    swiperInstances.all.push(swiperInstance);

    return swiperInstance;
}

function initOptionalDesktopSlider() {
    const onDesktop = window.matchMedia('(min-width: 768px)').matches;

    if (onDesktop) {
        const elements = document.querySelectorAll('.js-slider-on-desktop');
        if (elements.length === 0) {
            return;
        }

        elements.forEach((sliderElement) => {
            addSwiperMarkup(sliderElement);
            const swiperInstance = initSlider(sliderElement, 'swiper-slide', {
                slidesPerView: 3,
                spaceBetween: 20,
                navigation: false,
                breakpoints: {
                    1920: {
                        centeredSlides: false,
                    },
                },
                pagination: {
                    el: sliderElement.querySelector('.swiper-pagination'),
                    clickable: true,
                },
            });
            swiperInstances.desktopOnly.push(swiperInstance);
        });
    } else {
        destroySwiperInstances(swiperInstances.desktopOnly);
        removeSwiperMarkup(document.querySelectorAll('.js-slider-on-desktop:not(.js-slider-on-mobile)'));
    }
}

function initOptionalMobileSlider() {
    const onMobile = window.matchMedia('(max-width: 768px)').matches;

    if (onMobile) {
        const elements = document.querySelectorAll('.js-slider-on-mobile');

        elements.forEach((sliderElement) => {
            addSwiperMarkup(sliderElement);
            const additionalBreakPoint = sliderElement.classList.contains('js-slider-on-mobile--3')
                ? {
                      breakpoints: {
                          576: {
                              slidesPerView: 3,
                              spaceBetween: 15,
                          },
                      },
                  }
                : {};

            const swiperInstance = initSlider(sliderElement, 'swiper-slide', {
                slidesPerView: 1,
                spaceBetween: 20,
                navigation: false,
                ...additionalBreakPoint,
                pagination: {
                    el: sliderElement.querySelector('.swiper-pagination'),
                    clickable: true,
                },
            });
            swiperInstances.mobileOnly.push(swiperInstance);
        });
    } else {
        destroySwiperInstances(swiperInstances.mobileOnly);
        removeSwiperMarkup(document.querySelectorAll('.js-slider-on-mobile:not(.js-slider-on-desktop)'));
    }
}

function initTeaserCarousel() {
    const inBackend = document.body.classList.contains('neos-backend');
    const isFirefox = navigator.userAgent.search('Firefox') > -1;

    document.querySelectorAll('.teaser-carousel').forEach((sliderElement) => {
        initSlider(sliderElement, 'teaser-carousel-item', {
            // since the 3D effect interferes with
            // neos editable components (supposedly mouse events)
            // we remove the effect for Firefox in the backend
            effect: inBackend && isFirefox ? null : 'coverflow',
            slidesPerView: 1.5,
            centeredSlides: true,
            initialSlide: 1,
            spaceBetween: 40,
            coverflowEffect: {
                rotate: 0,
                stretch: 0,
                depth: 200,
                // effect modifier (float between 0 and 1)
                modifier: 1,
                slideShadows: true,
            },
            breakpoints: {
                1025: {
                    slidesPerView: 3,
                    spaceBetween: 75,
                    coverflowEffect: {
                        slideShadows: false,
                    },
                },
                768: {
                    slidesPerView: 3,
                    spaceBetween: 55,
                },
            },
            pagination: {
                el: sliderElement.querySelector('.swiper-pagination'),
                clickable: true,
            },
        });
    });
}

function initQuoteSlider() {
    document.querySelectorAll('.quote-slider-container').forEach((container) => {
        const sliderEl = container.querySelector('.quote-slider__slider');

        if (!sliderEl) {
            return;
        }

        initSlider(sliderEl, 'quote-item', {
            ...defaultSwiperOptions,
            slidesPerView: 1,
            spaceBetween: 10,
            autoHeight: true,

            navigation: {
                nextEl: container.querySelector('.swiper-button-next'),
                prevEl: container.querySelector('.swiper-button-prev'),
            },
            pagination: {
                el: container.querySelector('.swiper-pagination'),
                dynamicBullets: true,
                clickable: true,
            },
        });
    });
}

function destroySwiperInstances(instances) {
    instances.forEach((swiperInstance) => {
        if (typeof swiperInstance.destroy === 'function') {
            swiperInstance.destroy();
        }
    });
    instances.splice(0, instances.length); // clear array in-place so re-init starts fresh
}

/**
 * used for optional sliders to add the swiper markup for the slider an pagination
 **/
function addSwiperMarkup(element) {
    const sliderWrapper = element.querySelector('.neos-contentcollection');
    if (!sliderWrapper) {
        return;
    }
    sliderWrapper.classList.add('swiper-wrapper');
    sliderWrapper.childNodes.forEach((childNode) => childNode.classList.add('swiper-slide'));

    let pagination = element.querySelector('.swiper-pagination');

    if (pagination === null) {
        pagination = document.createElement('div');
        pagination.classList.add('swiper-pagination');

        element.appendChild(pagination);
    }
}

function removeSwiperMarkup(elements) {
    [...elements].forEach((element) => {
        const sliderWrapper = element.querySelector('.neos-contentcollection');
        if (!sliderWrapper) {
            return;
        }

        const pagination = element.querySelector('.swiper-pagination');
        if (pagination) {
            pagination.remove();
        }

        sliderWrapper.classList.remove('swiper-wrapper');
        sliderWrapper.childNodes.forEach((childNode) => childNode.classList.remove('swiper-slide'));
    });
}

/**
 * Make sure sliders are destroyed and reinitialized
 * when a new node is created (moving nodes/slides does not seem to be a problem)
 *
 * We check if the parent of the created element has a ``swiper-wrapper`` class
 * if so it's pretty safe to assume that the created element is within a slider and we reinitialize
 * all sliders on the current page (there could be ways to reinitialize only the slider that was affected,
 * but the performance impact is probably negligible for most sites).
 */
document.addEventListener('Neos.NodeCreated', function (event) {
    if (event.detail.element) {
        if (event.detail.element.closest('.swiper-wrapper')) {
            destroySwiperInstances([
                ...swiperInstances.all,
                ...swiperInstances.desktopOnly,
                ...swiperInstances.mobileOnly,
            ]);
            initSlidersForNodes();
            initOptionalDesktopSlider();
            initOptionalMobileSlider();
            initTeaserCarousel();
            initQuoteSlider();
        }
    }
});

let delayed;
window.addEventListener('resize', () => {
    if (delayed) {
        clearTimeout(delayed);
    }
    delayed = setTimeout(() => {
        initOptionalMobileSlider();
        initOptionalDesktopSlider();
    }, 500);
});
